/**
 * PrizeContestantList — the contestants behind one event's prize numbers,
 * grouped by allocation status.
 *
 * Each group is a COLLAPSED disclosure by default. An event can carry 50+
 * entries per bucket; expanding an event to read its allocation panel should
 * not dump three long lists underneath it. The header line (icon + label +
 * count) is the default view, and the admin opens the pile they care about.
 *
 * The grouping is NOT derived here. Each entry arrives from the API already
 * carrying `status`, `exclusion_reason` and `flags`; this component only sorts
 * them into piles and renders the reasons it was given. See
 * types/prizeAllocation.ts for why.
 */

import { useState } from 'react';
import {
  ChevronRight,
  Lock,
  Clock,
  Ban,
  AlertTriangle,
  CreditCard,
  Banknote,
  Landmark,
  HelpCircle,
  UserCircle,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  PRIZE_STATUS_LABELS,
  PRIZE_STATUS_DESCRIPTIONS,
  PRIZE_STATUS_STYLES,
  PRIZE_EXCLUSION_REASON_LABELS,
  PRIZE_ENTRY_FLAG_LABELS,
  type PrizeAllocationStatus,
} from '@/types/prizeAllocation';
import type { PrizeOverviewEntry } from '@/api/extensions/usePrizeOverview';

const money = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_ICONS: Record<PrizeAllocationStatus, typeof Lock> = {
  locked: Lock,
  potential: Clock,
  excluded: Ban,
};

const GROUP_ORDER: PrizeAllocationStatus[] = ['locked', 'potential', 'excluded'];

/** Payment-method glyphs mirror the profile Financials tab's vocabulary. */
function PaymentMethodLabel({ method }: { method: string | null }) {
  if (!method) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <HelpCircle className="h-3 w-3" aria-hidden="true" />
        Not recorded
      </span>
    );
  }
  const map: Record<string, { icon: typeof CreditCard; label: string }> = {
    stripe_card: { icon: CreditCard, label: 'Card' },
    bank: { icon: Landmark, label: 'Bank (ACH)' },
    cash: { icon: Banknote, label: 'Cash' },
    check: { icon: Banknote, label: 'Check' },
    pay_later: { icon: Clock, label: 'Pay later' },
  };
  const entry = map[method];
  if (!entry) return <span className="capitalize">{method.replace(/_/g, ' ')}</span>;
  const Icon = entry.icon;
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className="h-3 w-3" aria-hidden="true" />
      {entry.label}
    </span>
  );
}

/** Innermost expander: what this contestant owes, paid, and how. */
function ContestantFeeDetail({ entry }: { entry: PrizeOverviewEntry }) {
  const fees = entry.fee_breakdown;
  const outstanding = Math.max(0, entry.amount_due - entry.amount_paid);

  return (
    <div className="border-t border-border/60 bg-muted/30 px-3 py-2.5">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">Payment status</dt>
          <dd className="font-medium capitalize">{entry.payment_status}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Paid how</dt>
          <dd className="font-medium">
            <PaymentMethodLabel method={entry.payment_method} />
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Amount due</dt>
          <dd className="font-medium tabular-nums">{money(entry.amount_due)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Amount paid</dt>
          <dd className="font-medium tabular-nums">{money(entry.amount_paid)}</dd>
        </div>

        {entry.amount_refunded > 0 && (
          <div>
            <dt className="text-muted-foreground">Refunded</dt>
            <dd className="font-medium tabular-nums text-red-600 dark:text-red-400">
              −{money(entry.amount_refunded)}
            </dd>
          </div>
        )}
        {entry.credit_applied > 0 && (
          <div>
            <dt className="text-muted-foreground">Credit applied</dt>
            <dd className="font-medium tabular-nums text-blue-600 dark:text-blue-400">
              {money(entry.credit_applied)}
            </dd>
          </div>
        )}
        {outstanding > 0.005 && (
          <div>
            <dt className="text-muted-foreground">Outstanding</dt>
            <dd className="font-medium tabular-nums text-amber-700 dark:text-amber-400">
              {money(outstanding)}
            </dd>
          </div>
        )}

        {fees && (
          <div className="col-span-2 sm:col-span-3">
            <dt className="text-muted-foreground">Fee breakdown</dt>
            <dd className="font-medium tabular-nums">
              {money(fees.entry_fee)} entry
              {fees.transaction_fee > 0 && ` + ${money(fees.transaction_fee)} transaction`}
              {fees.non_member_fee > 0 && ` + ${money(fees.non_member_fee)} non-member`}
              {' = '}
              {money(fees.total)}
            </dd>
          </div>
        )}

        {entry.paid_by && (
          <div className="col-span-2">
            <dt className="text-muted-foreground">Paid by</dt>
            <dd className="font-medium">{entry.paid_by.name}</dd>
          </div>
        )}
        {!entry.paid_by && entry.responsible_party && (
          <div className="col-span-2">
            <dt className="text-muted-foreground">Owed by</dt>
            <dd className="font-medium">{entry.responsible_party.name}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

/**
 * Per-contestant admin actions: open their user record, or become them.
 *
 * Both are deliberately icon-only and sit OUTSIDE the row's disclosure button —
 * nesting an interactive control inside another button is invalid, and clicking
 * "view user" must not also toggle the row open.
 */
function ContestantActions({
  entry,
  canImpersonate,
  currentUserProfileId,
  onImpersonate,
  impersonatePendingId,
}: {
  entry: PrizeOverviewEntry;
  canImpersonate: boolean;
  currentUserProfileId?: string;
  onImpersonate: (entry: PrizeOverviewEntry) => void;
  impersonatePendingId: string | null;
}) {
  const userId = entry.user_profile_id;
  if (!userId) return null;

  // Impersonating yourself is a no-op that looks like a bug.
  const showImpersonate = canImpersonate && userId !== currentUserProfileId;
  const isPending = impersonatePendingId === entry.registration_id;

  return (
    <TooltipProvider>
      <span className="flex shrink-0 items-center gap-0.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button asChild size="sm" variant="ghost" className="h-7 w-7 p-0">
              {/* ?tab=entries lands the admin on this contestant's entries,
                  which is the context they came from. */}
              <Link
                to={`/users/${userId}?tab=entries`}
                aria-label={`View ${entry.contestant_name}'s user details`}
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent>View user details</TooltipContent>
        </Tooltip>

        {showImpersonate && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                disabled={isPending}
                onClick={() => onImpersonate(entry)}
                aria-label={`Log in as ${entry.contestant_name}`}
              >
                {isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <UserCircle className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Log in as this user</TooltipContent>
          </Tooltip>
        )}
      </span>
    </TooltipProvider>
  );
}

interface EntryRowProps {
  entry: PrizeOverviewEntry;
  onToggleExclusion: (entry: PrizeOverviewEntry, excluded: boolean) => void;
  isTogglePending: boolean;
  canImpersonate: boolean;
  currentUserProfileId?: string;
  onImpersonate: (entry: PrizeOverviewEntry) => void;
  impersonatePendingId: string | null;
}

function EntryRow({
  entry,
  onToggleExclusion,
  isTogglePending,
  canImpersonate,
  currentUserProfileId,
  onImpersonate,
  impersonatePendingId,
}: EntryRowProps) {
  const [expanded, setExpanded] = useState(false);
  const styles = PRIZE_STATUS_STYLES[entry.status];

  // The reason copy: the API tells us why. We only choose the wording.
  const reasonLine = entry.exclusion_reason
    ? PRIZE_EXCLUSION_REASON_LABELS[entry.exclusion_reason]
    : entry.flags.length > 0
      ? entry.flags.map((f) => PRIZE_ENTRY_FLAG_LABELS[f]).join(' · ')
      : null;

  const extraReason = entry.drawn_out_reason || entry.unplaced_reason;
  const isExcludedFromPool =
    entry.status === 'excluded' && entry.exclusion_reason === 'removed_from_prize_pool';
  // A cancelled entry is already out of the pool for a reason the toggle can't
  // undo — offering the switch there would be a no-op that looks like a lever.
  const canToggle = entry.exclusion_reason !== 'cancelled';

  return (
    <div className="overflow-hidden rounded-md border border-border/60 bg-background">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <ChevronRight
            className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${
              expanded ? 'rotate-90' : ''
            }`}
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="truncate text-sm font-medium">{entry.contestant_name}</span>
              {entry.draw_position !== null && (
                <span className="text-xs text-muted-foreground">
                  #{entry.draw_position}
                </span>
              )}
              {entry.needs_review && (
                <span className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  Needs review
                </span>
              )}
            </span>
            {(reasonLine || extraReason) && (
              <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                {reasonLine}
                {extraReason && ` — ${extraReason}`}
              </span>
            )}
          </span>
        </button>

        <span className="shrink-0 text-right text-xs">
          <span className="block font-medium tabular-nums">{money(entry.amount_paid)}</span>
          <span className={`block capitalize ${styles.text}`}>{entry.payment_status}</span>
        </span>

        <ContestantActions
          entry={entry}
          canImpersonate={canImpersonate}
          currentUserProfileId={currentUserProfileId}
          onImpersonate={onImpersonate}
          impersonatePendingId={impersonatePendingId}
        />
      </div>

      {expanded && (
        <>
          <ContestantFeeDetail entry={entry} />
          {canToggle && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 bg-muted/30 px-3 py-2">
              <span className="text-xs text-muted-foreground">
                {isExcludedFromPool
                  ? 'This entry is not counted in the prize pool.'
                  : 'This entry is counted in the prize pool.'}
              </span>
              <Button
                size="sm"
                variant={isExcludedFromPool ? 'outline' : 'destructive'}
                disabled={isTogglePending}
                onClick={() => onToggleExclusion(entry, !isExcludedFromPool)}
              >
                {isExcludedFromPool ? 'Add back to prize pool' : 'Remove from prize pool'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export interface PrizeContestantListProps {
  entries: PrizeOverviewEntry[];
  onToggleExclusion: (entry: PrizeOverviewEntry, excluded: boolean) => void;
  isTogglePending: boolean;
  canImpersonate: boolean;
  currentUserProfileId?: string;
  onImpersonate: (entry: PrizeOverviewEntry) => void;
  impersonatePendingId: string | null;
}

export function PrizeContestantList({
  entries,
  onToggleExclusion,
  isTogglePending,
  canImpersonate,
  currentUserProfileId,
  onImpersonate,
  impersonatePendingId,
}: PrizeContestantListProps) {
  // Collapsed by default — the counts are the summary; the lists are on demand.
  const [openGroups, setOpenGroups] = useState<Set<PrizeAllocationStatus>>(new Set());

  const toggleGroup = (status: PrizeAllocationStatus) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });

  if (entries.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        No contestants have entered this event yet.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {GROUP_ORDER.map((status) => {
        const group = entries.filter((e) => e.status === status);
        const needsReview = group.filter((e) => e.needs_review).length;
        // Render every bucket, even at zero — an admin needs to see that
        // "0 not included" is a fact, not a section that failed to load.
        const Icon = STATUS_ICONS[status];
        const styles = PRIZE_STATUS_STYLES[status];
        const isOpen = openGroups.has(status);
        const isEmpty = group.length === 0;

        return (
          <section
            key={status}
            aria-label={PRIZE_STATUS_LABELS[status]}
            className="overflow-hidden rounded-md border border-border/60"
          >
            <button
              type="button"
              onClick={() => !isEmpty && toggleGroup(status)}
              aria-expanded={isOpen}
              disabled={isEmpty}
              className="flex w-full items-center gap-2 bg-muted/40 px-3 py-2 text-left transition-colors enabled:hover:bg-muted disabled:cursor-default"
            >
              <ChevronRight
                className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${
                  isOpen ? 'rotate-90' : ''
                } ${isEmpty ? 'opacity-0' : ''}`}
                aria-hidden="true"
              />
              <Icon className={`h-3.5 w-3.5 shrink-0 ${styles.text}`} aria-hidden="true" />
              <span className={`text-xs font-semibold ${styles.text}`}>
                {PRIZE_STATUS_LABELS[status]}
              </span>
              <span className="text-xs text-muted-foreground">({group.length})</span>

              {needsReview > 0 && (
                <span className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  {needsReview} to review
                </span>
              )}

              <span className="ml-auto hidden truncate text-xs text-muted-foreground sm:block">
                {PRIZE_STATUS_DESCRIPTIONS[status]}
              </span>
            </button>

            {isOpen && !isEmpty && (
              <div className="space-y-1.5 border-t border-border/60 p-2">
                {group.map((entry) => (
                  <EntryRow
                    key={entry.registration_id}
                    entry={entry}
                    onToggleExclusion={onToggleExclusion}
                    isTogglePending={isTogglePending}
                    canImpersonate={canImpersonate}
                    currentUserProfileId={currentUserProfileId}
                    onImpersonate={onImpersonate}
                    impersonatePendingId={impersonatePendingId}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
